import { useEffect, useState } from "react";
import { Play, Pause } from "lucide-react";
import Reveal from "@/components/Reveal";
import { startSession, stopSession } from "@/lib/meditationEngine";

const ACCENTS = {
  cyan: "text-cyan-300 border-cyan-300/30 bg-cyan-300/10",
  violet: "text-violet-300 border-violet-400/30 bg-violet-400/10",
  gold: "text-amber-300 border-amber-300/30 bg-amber-300/10",
  magenta: "text-fuchsia-300 border-fuchsia-400/30 bg-fuchsia-400/10",
};

const INNER_SESSIONS = [
  {
    id: "stillness",
    n: "01",
    freq: 432,
    binaural: 4,
    title: "STILLNESS",
    desc: "Ground into the present moment. A 432Hz foundation with a slow theta drift for settling the nervous system.",
    accent: "cyan",
  },
  {
    id: "healing",
    n: "02",
    freq: 528,
    binaural: 5,
    title: "HEALING",
    desc: "The 528Hz transformation tone — soften the chest, release, and let old energy unwind.",
    accent: "gold",
  },
  {
    id: "intuition",
    n: "03",
    freq: 741,
    binaural: 6.3,
    title: "INTUITION",
    desc: "741Hz for clearing mental fog and reconnecting with the quiet voice underneath the noise.",
    accent: "violet",
  },
  {
    id: "awakening",
    n: "04",
    freq: 963,
    binaural: 7.83,
    title: "AWAKENING",
    desc: "963Hz crown frequency — spacious, bright, expansive. Sit tall, breathe slow, listen upward.",
    accent: "magenta",
  },
];

export const ENERGY_SESSIONS = [
  {
    id: "foundation",
    n: "01",
    freq: 174,
    binaural: 3,
    title: "FOUNDATION",
    desc: "The deepest Solfeggio tone — 174Hz wraps you in safety and gives your energy a place to land.",
    accent: "cyan",
  },
  {
    id: "restoration",
    n: "02",
    freq: 285,
    binaural: 4.5,
    title: "RESTORATION",
    desc: "285Hz is linked with the renewal of energy fields — a slow, warm current for tired systems.",
    accent: "gold",
  },
  {
    id: "release",
    n: "03",
    freq: 396,
    binaural: 5.5,
    title: "RELEASE",
    desc: "396Hz for loosening fear and stored tension. Let the low pulse carry out what you no longer need.",
    accent: "violet",
  },
  {
    id: "insight",
    n: "04",
    freq: 852,
    binaural: 6.8,
    title: "INSIGHT",
    desc: "852Hz for clearing the inner channel — bright, crystalline, and quietly electric.",
    accent: "magenta",
  },
];

const DURATIONS = [5, 10, 20];

const fmt = (s) => {
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
};

const notify = (src) =>
  window.dispatchEvent(new CustomEvent("shift-audio-session", { detail: { src } }));

export default function MeditationLibrary({
  prefix = "inner",
  eyebrow = "Listen Within",
  headingA = "Frequency",
  headingB = "Meditation",
  body = "Press play, put on headphones, and let the frequency do the heavy lifting. Each soundscape is tuned to a Solfeggio frequency and generated live in your browser — pure tone, slow breath, nothing to download.",
  sessions = INNER_SESSIONS,
}) {
  const t = (id) => `${prefix}-${id}`;
  const [activeId, setActiveId] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [mins, setMins] = useState(() => Object.fromEntries(sessions.map((s) => [s.id, 10])));

  useEffect(() => {
    if (!playing || !activeId) return undefined;
    const id = setInterval(() => setElapsed((e) => e + 0.25), 250);
    return () => clearInterval(id);
  }, [playing, activeId]);

  useEffect(() => {
    if (!activeId) return;
    const total = mins[activeId] * 60;
    if (elapsed >= total) {
      stopSession(3);
      setPlaying(false);
      setActiveId(null);
      setElapsed(0);
    }
  }, [elapsed, activeId, mins]);

  useEffect(() => {
    const onOther = (e) => {
      if (e.detail.src === prefix) return;
      setPlaying(false);
      setActiveId(null);
      setElapsed(0);
    };
    window.addEventListener("shift-audio-session", onOther);
    return () => window.removeEventListener("shift-audio-session", onOther);
  }, [prefix]);

  useEffect(() => () => stopSession(0.5), []);

  const toggle = (s) => {
    if (activeId === s.id) {
      if (playing) {
        stopSession(1.2);
        setPlaying(false);
      } else {
        startSession({ freq: s.freq, binaural: s.binaural });
        setPlaying(true);
      }
      notify(prefix);
      return;
    }
    startSession({ freq: s.freq, binaural: s.binaural });
    setActiveId(s.id);
    setElapsed(0);
    setPlaying(true);
    notify(prefix);
  };

  return (
    <div data-testid={t("meditation-library")} className="mt-24 text-left">
      <Reveal>
        <p className="eyebrow mb-5" data-testid={t("meditation-eyebrow")}>{eyebrow}</p>
        <h3
          data-testid={t("meditation-heading")}
          className="font-display max-w-2xl text-2xl font-bold uppercase leading-tight tracking-[0.12em] text-slate-100 sm:text-3xl"
        >
          {headingA} <span className="text-glow-cyan text-cyan-300">{headingB}</span>
        </h3>
        <p className="mt-6 max-w-xl text-sm leading-relaxed text-slate-400 sm:text-base" data-testid={t("meditation-body")}>
          {body}
        </p>
      </Reveal>

      <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {sessions.map((s, i) => {
          const isActive = activeId === s.id;
          const isPlaying = isActive && playing;
          const total = mins[s.id] * 60;
          const pct = isActive ? Math.min(100, (elapsed / total) * 100) : 0;
          return (
            <Reveal key={s.id} delay={i * 0.1}>
              <article
                data-testid={t(`meditation-card-${s.id}`)}
                className="cosmic-card flex h-full flex-col p-7"
              >
                <div className="flex items-start justify-between">
                  <span
                    className={`flex h-11 w-11 items-center justify-center rounded-full border font-display text-[10px] tracking-widest ${ACCENTS[s.accent]}`}
                  >
                    {s.freq}
                  </span>
                  <span className="font-display text-xs tracking-[0.3em] text-slate-600">{s.n}</span>
                </div>
                <h4 className="font-display mt-6 text-sm font-bold tracking-[0.22em] text-slate-100">
                  {s.title}
                </h4>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-400">{s.desc}</p>

                <div className="mt-6 flex items-center gap-2" data-testid={t(`meditation-durations-${s.id}`)}>
                  {DURATIONS.map((m) => (
                    <button
                      key={m}
                      data-testid={t(`meditation-duration-${s.id}-${m}`)}
                      disabled={isActive}
                      onClick={() => setMins((prev) => ({ ...prev, [s.id]: m }))}
                      className={`rounded-full border px-3 py-1 font-display text-[10px] tracking-[0.2em] transition-colors duration-300 ${
                        mins[s.id] === m
                          ? "border-cyan-300/50 text-cyan-200"
                          : "border-white/10 text-slate-500 hover:text-slate-300"
                      } ${isActive ? "cursor-not-allowed opacity-50" : ""}`}
                    >
                      {m}M
                    </button>
                  ))}
                </div>

                <div className="mt-6 flex items-center gap-4">
                  <button
                    data-testid={t(`meditation-play-${s.id}`)}
                    onClick={() => toggle(s)}
                    aria-label={isPlaying ? `Pause ${s.title}` : `Play ${s.title}`}
                    className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full transition-all duration-500 ${
                      isPlaying
                        ? "bg-gradient-to-br from-cyan-300 to-violet-500 text-[#040308] shadow-[0_0_30px_rgba(0,245,212,0.5)]"
                        : "border border-cyan-300/40 text-cyan-200 hover:shadow-[0_0_24px_rgba(0,245,212,0.35)]"
                    }`}
                  >
                    {isPlaying ? (
                      <Pause className="h-4 w-4" strokeWidth={2} />
                    ) : (
                      <Play className="ml-0.5 h-4 w-4" strokeWidth={2} />
                    )}
                  </button>
                  <div className="flex-1">
                    <div
                      className="h-1 overflow-hidden rounded-full bg-white/10"
                      data-testid={t(`meditation-progress-${s.id}`)}
                    >
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-cyan-300 to-violet-500 transition-[width] duration-300"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <p
                      className="font-display mt-2 text-[10px] tracking-[0.25em] text-slate-500"
                      data-testid={t(`meditation-timer-${s.id}`)}
                    >
                      {isActive ? `${fmt(elapsed)} / ${fmt(total)}` : `${fmt(total)} SESSION`}
                    </p>
                  </div>
                </div>
              </article>
            </Reveal>
          );
        })}
      </div>

      <Reveal delay={0.2}>
        <p
          className="font-display mt-8 text-[10px] tracking-[0.3em] text-slate-600"
          data-testid={t("meditation-note")}
        >
          HEADPHONES RECOMMENDED — BINAURAL SOUNDSCAPES GENERATED LIVE IN YOUR BROWSER
        </p>
      </Reveal>
    </div>
  );
}
